<template>
  <v-card outlined class="ai-action-card" :data-status="status" data-testid="ai-action-card">
    <div class="d-flex flex-wrap align-center px-4 pt-3">
      <v-icon small class="mr-2" color="primary">mdi-flash-outline</v-icon>
      <span class="ai-action-card__title font-weight-medium" data-testid="ai-action-title">{{ title }}</span>
      <v-spacer />
      <v-chip x-small :color="meta.color" text-color="white" data-testid="ai-action-status">
        {{ $t(meta.key) }}
      </v-chip>
    </div>

    <div v-if="effectNode" class="ai-action-card__effect px-4 pt-2" data-testid="ai-action-effect">
      <agent-operation-effect :node="effectNode" />
    </div>

    <div v-if="noteKey" class="px-4 pt-2 text-caption text--secondary" data-testid="ai-action-note">
      {{ $t(noteKey) }}
    </div>

    <div v-if="status === 'failed'" class="px-4 pt-2 text-caption error--text" data-testid="ai-action-failure">
      {{ $t(failureTextKey) }}
      <code v-if="localOp.error_code && !outcomeUnknown" class="ml-1" data-testid="ai-action-error-code">{{ localOp.error_code }}</code>
    </div>

    <div v-if="decisionErrorKey" class="px-4 pt-2 text-caption warning--text" data-testid="ai-action-decision-error">
      {{ $t(decisionErrorKey) }}
    </div>

    <div class="d-flex flex-wrap align-center px-4 py-3">
      <template v-if="canDecide">
        <v-btn small depressed color="primary" class="mr-2" :loading="busy === 'approve'"
          :disabled="!!busy || disabled" data-testid="ai-action-approve" @click="decide('approve')">
          {{ $t("ai_assistant.action_card.approve") }}
        </v-btn>
        <v-btn small outlined color="error" :loading="busy === 'reject'" :disabled="!!busy || disabled"
          data-testid="ai-action-reject" @click="decide('reject')">
          {{ $t("ai_assistant.action_card.reject") }}
        </v-btn>
      </template>
      <v-btn v-else-if="link && status === 'done'" small depressed color="primary" v-bind="link" data-testid="ai-action-open">
        <v-icon left small>mdi-open-in-new</v-icon>
        {{ $t("ai_assistant.action_card.open") }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
// FT-199 STEP-10: карточка операции Ren в чате. Ren готовит запись (инструмент записи отвечает pending_approval), человек
// подтверждает её кнопкой здесь — «Выполнить» (approve) или «Отмена» (reject). Решение идёт тем же API, что и экран
// «Ожидают одобрения» (POST /api/v1/agent_operations/:id/approve|reject), итог показывается тем же рендером эффекта.
//
// Источник состояния — сообщение беседы `role: "action"` (его перечитывает AiAssistant); карточка держит копию
// операции лишь затем, чтобы показать ответ на решение, не дожидаясь перечитывания. Родителю она сообщает событием
// `refresh` — после решения и при опросе. Опрос нужен для `accepted`: операция выполняется асинхронно, и в канал
// статус не приходит (канал несёт только факт появления карточки).
import axios from "axios";
import AgentOperationEffect from "@/components/agents/AgentOperationEffect.vue";
import { humanizeKey } from "@/components/agents/agentOperationEffect";
import { effectTreeFor } from "@/components/agents/agentOperationEffectTree";
import { API_PATH, outcomeForDecisionError } from "@/components/agents/agentOperationOutcome";
import {
  STATUS_NOTE_KEYS, statusMeta, isPollable, pollDelay, canDecide, isOutcomeUnknown,
  failureKey, resultLink,
} from "./aiActionCard";

export default {
  name: "AiActionCard",
  components: { AgentOperationEffect },
  props: {
    operation: { type: Object, required: true },
    // родитель занят пачкой — кнопки одной карточки на это время заперты
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      localOp: { ...this.operation },
      busy: null,
      decisionErrorKey: null,
      pollTimer: null,
      pollStartedAt: null,
    };
  },
  computed: {
    status() {
      return this.localOp.status;
    },
    meta() {
      return statusMeta(this.status);
    },
    canDecide() {
      return canDecide(this.localOp);
    },
    outcomeUnknown() {
      return isOutcomeUnknown(this.localOp);
    },
    failureTextKey() {
      return failureKey(this.localOp);
    },
    noteKey() {
      return STATUS_NOTE_KEYS[this.status] || null;
    },
    link() {
      return resultLink(this.localOp.result_ui_url);
    },
    // Название операции: tool_title от сервера (на языке интерфейса, CTR-03), иначе имя инструмента словами.
    title() {
      const op = this.localOp;
      if (op.tool_title) return op.tool_title;
      return op.tool ? humanizeKey(op.tool) : "";
    },
    effectNode() {
      const effect = this.localOp.effect;
      if (effect === null || effect === undefined) return null;
      return effectTreeFor(this, effect);
    },
  },
  watch: {
    // Перечитанное сообщение приходит новым объектом операции — он главнее локальной копии.
    operation(next) {
      this.localOp = { ...next };
    },
    // immediate: карточка, пришедшая уже в `accepted`, начинает опрос сразу
    status: {
      immediate: true,
      handler(next) {
        if (isPollable(next)) this.startPolling();
        else this.stopPolling();
      },
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async decide(decision) {
      if (this.busy || !this.canDecide) return;
      this.busy = decision;
      this.decisionErrorKey = null;
      try {
        const { data } = await axios.post(`${API_PATH}/${this.localOp.id}/${decision}`);
        const row = data && data.data;
        if (row) this.applyRow(row);
      } catch (err) {
        // тот же разбор ошибки решения, что у экрана одобрения; 409 несёт нынешний статус операции
        const { outcome, row, status } = outcomeForDecisionError(err);
        if (row) this.applyRow(row);
        else if (status) this.localOp = { ...this.localOp, status };
        this.decisionErrorKey = outcome.key;
      } finally {
        this.busy = null;
        // Строка ответа не несёт result_ui_url и прочего — итог берём из перечитанного сообщения.
        this.$emit("refresh");
      }
    },
    // Строка операции из ответа на решение → то, что карточке нужно показать сразу.
    applyRow(row) {
      this.localOp = {
        ...this.localOp,
        status: row.status || this.localOp.status,
        error_code: row.error_code === undefined ? this.localOp.error_code : row.error_code,
      };
    },
    startPolling() {
      if (this.pollTimer !== null) return;
      this.pollStartedAt = Date.now();
      this.schedulePoll();
    },
    schedulePoll() {
      this.pollTimer = setTimeout(() => this.poll(), pollDelay(Date.now() - this.pollStartedAt));
    },
    poll() {
      this.pollTimer = null;
      if (!isPollable(this.status)) return;
      this.$emit("refresh");
      this.schedulePoll();
    },
    stopPolling() {
      if (this.pollTimer !== null) clearTimeout(this.pollTimer);
      this.pollTimer = null;
    },
  },
};
</script>

<style scoped>
.ai-action-card {
  width: 100%;
  max-width: 560px;
}
.ai-action-card__title,
.ai-action-card__effect {
  overflow-wrap: anywhere;
}
</style>
